import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Collapse, IconButton, Paper, Typography } from '@mui/material';
import React, { useState } from 'react';

export interface DetailSectionProps {
  title: string | React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  headerAction?: React.ReactNode;
  className?: string;
  /** Additional sx props merged into the root Paper element */
  sx?: object;
}

const DetailSection: React.FC<DetailSectionProps> = ({
  title,
  icon,
  children,
  collapsible = false,
  defaultExpanded = true,
  headerAction,
  className,
  sx,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const titleNode =
    typeof title === 'string' ? (
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 'var(--font-weight-semibold)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--ls-micro)',
        }}
      >
        {title}
      </Typography>
    ) : (
      title
    );

  return (
    <Paper
      className={className}
      variant="outlined"
      sx={{
        borderRadius: 'var(--border-radius-lg)',
        mb: 2,
        // The space between sections, not after the last one: a trailing margin added 16px to the
        // page's bottom gap wherever a section ended the page.
        '&:last-child': { mb: 0 },
        overflow: 'hidden',
        border: '1px solid var(--color-border-light)',
        boxShadow: 'var(--shadow-sm)',
        bgcolor: 'var(--color-background)',
        ...sx,
      }}
    >
      {/* Section header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          bgcolor: 'var(--color-background-page)',
          borderBottom:
            !collapsible || expanded ? '1px solid var(--color-border-light)' : 'none',
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
          minHeight: 48,
        }}
        onClick={collapsible ? () => setExpanded((v) => !v) : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && (
            <Box sx={{ color: 'var(--acc)', display: 'flex', alignItems: 'center' }}>
              {icon}
            </Box>
          )}
          {titleNode}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {headerAction}
          {collapsible && (
            <IconButton
              size="small"
              sx={{ color: 'var(--color-text-muted)', ml: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              {expanded ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Section content */}
      {collapsible ? (
        <Collapse in={expanded}>
          <Box sx={{ p: 2.5 }}>{children}</Box>
        </Collapse>
      ) : (
        <Box sx={{ p: 2.5 }}>{children}</Box>
      )}
    </Paper>
  );
};

export default DetailSection;
